import type { RulePromotionEvaluation } from "@/lib/discount-evaluation/types"
import { formatBRL } from "@/lib/format-brl"
import { formatPoints } from "@/lib/format-points"
import { LOYALTY_RULE_PROMO_PREFIX } from "@/lib/loyalty-benefits"
import { emptyRewardsState, reservedRewardPoints, type RewardsState } from "@/lib/loyalty-rewards"
import type { Discount } from "@/types/external"
import type { ActiveBenefits, Benefit, EntitlementGrant } from "@/types/loyalty-benefits"

/** What kind of loyalty mechanic gave a discount; picks the row's icon. */
export type LoyaltySourceKind = "points" | "reward" | "benefit" | "promotion"

/** One loyalty discount of the order in the customer's words: where it comes from, why, and the points left when it spends points. */
export type LoyaltySourceRow = {
  key: string
  kind: LoyaltySourceKind
  label: string
  reason: string | null
  summaryLabel: string
  value: number
  remainingPoints?: number
}

/** The loyalty discounts on the order plus the loyalty data the checkout already loaded. */
export type LoyaltySourceInput = {
  benefitDiscounts: readonly Discount[]
  pointsUsed: number
  pointsDiscount: number
  active: ActiveBenefits | null
  grants: readonly EntitlementGrant[]
  rewards: RewardsState | undefined
  pointsBalance: number
  rulePromotions: RulePromotionEvaluation | null
}

type CodeTotal = { code: string; value: number; target: Discount["target"] }

type Described = Omit<LoyaltySourceRow, "key" | "value" | "remainingPoints">

const GENERIC_LABEL = "Benefício fidelidade"
const REWARD_PREFIX = "LOYALTY_REWARD:"
const CART_THRESHOLD_PREFIX = "LOYALTY_CART_THRESHOLD:"
const ITEM_PROMO_PREFIX = "LOYALTY_ITEM_PROMO:"

const generic: Described = { kind: "benefit", label: GENERIC_LABEL, reason: null, summaryLabel: GENERIC_LABEL }

function capitalize(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1)
}

function roundCents(value: number): number {
  return Math.round(value * 100) / 100
}

function ordersWord(count: number): string {
  return count === 1 ? "1 pedido" : `${count} pedidos`
}

function benefitPhrase(benefit: Benefit | undefined, value: number): string {
  if (benefit?.freeProduct) return `1 ${benefit.freeProduct.item.name?.trim() || "produto"} grátis`
  if (benefit?.shipping?.type === "FREE_SHIPPING") return "frete grátis"
  if (benefit?.shipping) return `${formatBRL(value)} de desconto no frete`
  return `${formatBRL(value)} de desconto`
}

function codeTotals(discounts: readonly Discount[]): CodeTotal[] {
  const totals = new Map<string, CodeTotal>()
  for (const discount of discounts) {
    const shares = discount.sponsorshipValues?.length
      ? discount.sponsorshipValues.map((share) => ({ code: share.discountCode ?? "", value: share.amount?.value ?? 0 }))
      : [{ code: "", value: discount.amount?.value ?? 0 }]
    for (const share of shares) {
      if (share.value <= 0) continue
      const existing = totals.get(share.code)
      totals.set(share.code, { code: share.code, value: (existing?.value ?? 0) + share.value, target: existing?.target ?? discount.target })
    }
  }
  return [...totals.values()]
}

function describeCartThreshold(code: string, value: number, active: ActiveBenefits | null): Described {
  const threshold = active?.cartThreshold
  if (!threshold || threshold.id !== code.slice(CART_THRESHOLD_PREFIX.length)) return generic
  const name = threshold.name?.trim() || GENERIC_LABEL
  return {
    kind: "benefit",
    label: name,
    reason: `Pedido de ${formatBRL(threshold.minItemsSubtotal.value)} ou mais: ${benefitPhrase(threshold.benefit, value)}`,
    summaryLabel: name,
  }
}

function describeItemPromo(code: string, value: number, active: ActiveBenefits | null): Described {
  const [promoId = "", tierId = ""] = code.slice(ITEM_PROMO_PREFIX.length).split(":")
  const promo = active?.itemPromos?.find((candidate) => candidate.id === promoId)
  const tier = promo?.tiers.find((candidate) => candidate.id === tierId)
  if (!promo || !tier) return generic
  const name = promo.name?.trim() || GENERIC_LABEL
  const item = promo.triggerItem.name?.trim()
  const condition = item ? `${formatBRL(tier.minItemSpend.value)} ou mais em ${item}` : `${formatBRL(tier.minItemSpend.value)} ou mais no item da promoção`
  return { kind: "benefit", label: name, reason: `${condition}: ${benefitPhrase(tier.benefit, value)}`, summaryLabel: name }
}

function describeEntitlement(grant: EntitlementGrant, value: number, active: ActiveBenefits | null): Described {
  const phrase = benefitPhrase(grant.benefit, value)
  if (grant.source === "RECURRENCE") {
    const recurrence = active?.recurrence
    const required = recurrence && recurrence.id === grant.sourceConfigId ? recurrence.requiredOrders : 0
    if (required <= 0) return { ...generic, reason: capitalize(phrase) }
    return { kind: "benefit", label: `Você completou ${ordersWord(required)}`, reason: capitalize(phrase), summaryLabel: `Fidelidade (${ordersWord(required)})` }
  }
  const promo = active?.itemPromos?.find((candidate) => candidate.id === grant.sourceConfigId)
  const name = promo?.name?.trim()
  if (!promo || !name) return { ...generic, reason: capitalize(phrase) }
  const item = promo.triggerItem.name?.trim()
  return {
    kind: "benefit",
    label: name,
    reason: item ? `Compras acumuladas de ${item}: ${phrase}` : capitalize(phrase),
    summaryLabel: name,
  }
}

function describeRulePromotion(code: string, target: Discount["target"], rulePromotions: RulePromotionEvaluation | null): Described {
  const promotionId = code.slice(LOYALTY_RULE_PROMO_PREFIX.length).split(":")[0] ?? ""
  const name = rulePromotions?.applied.find((applied) => applied.promotionId === promotionId)?.promotionName?.trim() || "Promoção da loja"
  return {
    kind: "promotion",
    label: name,
    reason: target === "DELIVERY_FEE" ? "Desconto automático no frete" : "Desconto automático da loja",
    summaryLabel: name,
  }
}

function describeReward(code: string, rewards: RewardsState): Described {
  const rewardId = code.slice(REWARD_PREFIX.length)
  const reward = rewards.available.find((candidate) => candidate.id === rewardId)
  const quantity = rewards.redeemed.find((pick) => pick.rewardId === rewardId)?.quantity ?? 0
  const name = reward?.item.name?.trim()
  if (!name || quantity <= 0) return { kind: "reward", label: "Resgate com pontos", reason: null, summaryLabel: "Resgate com pontos" }
  return { kind: "reward", label: "Prêmio da fidelidade", reason: `${quantity} ${name} grátis`, summaryLabel: "Prêmio fidelidade" }
}

function describe(total: CodeTotal, input: LoyaltySourceInput, rewards: RewardsState): Described {
  const { code, value, target } = total
  if (code.startsWith(REWARD_PREFIX)) return describeReward(code, rewards)
  if (code.startsWith(LOYALTY_RULE_PROMO_PREFIX)) return describeRulePromotion(code, target, input.rulePromotions)
  if (code.startsWith(CART_THRESHOLD_PREFIX)) return describeCartThreshold(code, value, input.active)
  if (code.startsWith(ITEM_PROMO_PREFIX)) return describeItemPromo(code, value, input.active)
  const grant = code ? input.grants.find((candidate) => candidate.discountCode === code) : undefined
  return grant ? describeEntitlement(grant, value, input.active) : generic
}

function knownBalance(pointsBalance: number, rewards: RewardsState): number | null {
  if (pointsBalance > 0) return pointsBalance
  if (rewards.balance > 0) return rewards.balance
  return null
}

function pointsLeft(input: LoyaltySourceInput, rewards: RewardsState): number | undefined {
  const balance = knownBalance(input.pointsBalance, rewards)
  if (balance === null) return undefined
  return Math.max(0, balance - Math.max(0, input.pointsUsed) - reservedRewardPoints(rewards))
}

function withSummarySuffixes(rows: LoyaltySourceRow[]): LoyaltySourceRow[] {
  const rewardRows = rows.filter((row) => row.kind === "reward" && row.reason)
  if (rewardRows.length < 2) return rows
  return rows.map((row) => (row.kind === "reward" && row.reason ? { ...row, summaryLabel: `${row.summaryLabel} (${row.reason})` } : row))
}

/**
 * Names where each loyalty discount of the order comes from: the points traded for money, every benefit by its
 * mechanic (cart threshold, item promo, unlocked entitlement, rule promotion) and every reward redeemed with points,
 * plus the points left when a balance is known. A code the loaded data does not explain keeps the generic label.
 */
export function loyaltySourceRows(input: LoyaltySourceInput): LoyaltySourceRow[] {
  const rewards = input.rewards ?? emptyRewardsState
  const remaining = pointsLeft(input, rewards)
  const rows: LoyaltySourceRow[] = []

  if (input.pointsUsed > 0 && input.pointsDiscount > 0) {
    const points = formatPoints(input.pointsUsed, "pontos")
    rows.push({
      key: "points",
      kind: "points",
      label: input.active?.pointsProgram?.name?.trim() || "Programa de fidelidade",
      reason: `${points} trocados por ${formatBRL(input.pointsDiscount)}`,
      summaryLabel: `Fidelidade (${points})`,
      value: roundCents(input.pointsDiscount),
      ...(remaining === undefined ? {} : { remainingPoints: remaining }),
    })
  }

  for (const total of codeTotals(input.benefitDiscounts)) {
    const described = describe(total, input, rewards)
    const spendsPoints = described.kind === "reward"
    rows.push({
      ...described,
      key: total.code || `benefit-${rows.length}`,
      value: roundCents(total.value),
      ...(spendsPoints && remaining !== undefined ? { remainingPoints: remaining } : {}),
    })
  }

  return withSummarySuffixes(rows)
}

/** "Restam 40 pontos" for a row that spends points; null when the row spends none or the balance is unknown. */
export function remainingPointsText(row: Pick<LoyaltySourceRow, "remainingPoints">): string | null {
  if (row.remainingPoints === undefined) return null
  const amount = formatPoints(row.remainingPoints, "pontos")
  return row.remainingPoints === 1 ? `Resta ${amount}` : `Restam ${amount}`
}
